import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  LayoutGrid, CalendarDays, BarChart2, X, History, RefreshCw,
  MapPin, Package, AlertTriangle, CheckCircle2, Clock, TrendingUp
} from 'lucide-react';
import { exportToCsv, printReport, ExportPrintBar } from '../../utils/reportExport';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const ITEM_TYPES = ['Data Logger', 'Temperature Control Box', 'UN Approved Box', 'Mapping Report'];

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN');
}

function ExpiryBadge({ urgency, daysLeft }) {
  if (urgency === 'expired') return <Badge className="bg-red-600 text-white">Expired ({Math.abs(daysLeft)}d ago)</Badge>;
  if (urgency === 'critical') return <Badge className="bg-red-500 text-white">{daysLeft}d left</Badge>;
  if (urgency === 'warning') return <Badge className="bg-yellow-500 text-white">{daysLeft}d left</Badge>;
  return <Badge className="bg-blue-500 text-white">{daysLeft}d left</Badge>;
}

function CheckoutBadge({ status }) {
  if (status === 'Checked Out') return <Badge className="bg-orange-500 text-white">Checked Out</Badge>;
  return <Badge className="bg-green-500 text-white">Available</Badge>;
}

function ExpiryCellBadge({ status }) {
  if (status === 'Expired') return <Badge className="bg-red-500 text-white">Expired</Badge>;
  if (status === 'Expiring Soon') return <Badge className="bg-yellow-500 text-white">Expiring Soon</Badge>;
  return <Badge className="bg-green-500 text-white">Valid</Badge>;
}

// ─── History Drawer ───────────────────────────────────────────
function HistoryDrawer({ item, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    fetch(`${API}/api/reports/inventory/history/${item.id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md shadow-2xl flex flex-col h-full z-10 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-primary-blue text-white">
          <div>
            <h3 className="font-bold text-lg font-manrope">Item History</h3>
            <p className="text-white/70 text-sm font-ibm">{item.item_type} — {item.sr_no}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-text-secondary">Loading...</div>
          ) : !data || !data.history || data.history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-text-secondary gap-2">
              <History className="w-10 h-10 opacity-30" />
              <p className="text-sm">No checkout history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.history.map((h, i) => (
                <div key={i} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${h.type === 'Docket' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {h.type}
                    </span>
                    <Badge variant="outline" className="text-xs">{h.status}</Badge>
                  </div>
                  <p className="font-semibold text-text-dark font-ibm mt-1">{h.ref_no}</p>
                  {h.type === 'Trip' && h.from_location && (
                    <p className="text-xs text-text-secondary mt-1">{h.from_location} → {h.to_location}</p>
                  )}
                  {h.type === 'Docket' && h.shipment_type && (
                    <p className="text-xs text-text-secondary mt-1">{h.shipment_type}</p>
                  )}
                  <p className="text-xs text-text-secondary mt-1">{formatDate(h.date)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1: Status Board ───────────────────────────────────────
function StatusBoard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterCheckout, setFilterCheckout] = useState('');
  const [historyItem, setHistoryItem] = useState(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filterType) p.append('item_type', filterType);
    if (filterCheckout) p.append('checkout_status', filterCheckout);
    fetch(`${API}/api/reports/inventory/status?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterType, filterCheckout]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const STATUS_COLS = [
    { header: 'SR #', key: 'sr_no' },
    { header: 'Item Type', key: 'item_type' },
    { header: 'Manufacture', key: 'manufacture' },
    { header: 'Location', key: 'location' },
    { header: 'Expiry Date', key: 'expiry_date', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
    { header: 'Expiry Status', key: 'expiry_status' },
    { header: 'Checkout', key: 'checkout_status' },
    { header: 'Checked Out To', key: 'checked_out_to' },
  ];

  const handleExport = () => exportToCsv('Inventory_Status_Board', STATUS_COLS, items);
  const handlePrint = () => printReport('Inventory Status Board', STATUS_COLS, items);

  const total = items.length;
  const available = items.filter(i => i.checkout_status === 'Available').length;
  const checkedOut = items.filter(i => i.checkout_status === 'Checked Out').length;
  const expiring = items.filter(i => i.expiry_status !== 'Valid').length;

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: total, icon: Package, color: 'text-blue-600 bg-blue-50' },
          { label: 'Available', value: available, icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
          { label: 'Checked Out', value: checkedOut, icon: Clock, color: 'text-orange-600 bg-orange-50' },
          { label: 'Expiry Issues', value: expiring, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
        ].map(s => (
          <Card key={s.label} className="p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="w-5 h-5" /></div>
            <div>
              <p className="text-2xl font-bold text-text-dark font-manrope">{s.value}</p>
              <p className="text-xs text-text-secondary font-ibm">{s.label}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Item Type</label>
            <Select value={filterType || '__all__'} onValueChange={v => setFilterType(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Checkout Status</label>
            <Select value={filterCheckout || '__all__'} onValueChange={v => setFilterCheckout(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="free">Available Only</SelectItem>
                <SelectItem value="checked_out">Checked Out Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || items.length === 0} />
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['SR #', 'Item Type', 'Manufacture', 'Location', 'Expiry', 'Expiry Status', 'Checkout', 'Checked Out To', 'History'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">No items found</td></tr>
              ) : items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-text-dark font-ibm">{item.sr_no}</td>
                  <td className="px-4 py-3 text-text-dark">{item.item_type}</td>
                  <td className="px-4 py-3 text-text-dark">{item.manufacture || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-text-dark">
                      <MapPin className="w-3 h-3 text-text-secondary" />{item.location || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-dark whitespace-nowrap">{formatDate(item.expiry_date) !== '-' ? formatDate(item.expiry_date) : formatDate(item.validation_date)}</td>
                  <td className="px-4 py-3"><ExpiryCellBadge status={item.expiry_status} /></td>
                  <td className="px-4 py-3"><CheckoutBadge status={item.checkout_status} /></td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{item.checked_out_to || '-'}</td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost" size="sm"
                      data-testid={`history-btn-${item.id}`}
                      onClick={() => setHistoryItem(item)}
                      className="h-7 text-xs text-accent-blue hover:text-accent-blue"
                    >
                      <History className="w-3 h-3 mr-1" />View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <HistoryDrawer item={historyItem} onClose={() => setHistoryItem(null)} />
    </div>
  );
}

// ─── Tab 2: Expiry Calendar ────────────────────────────────────
function ExpiryCalendar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('60');
  const [filterType, setFilterType] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ days });
    if (filterType) p.append('item_type', filterType);
    fetch(`${API}/api/reports/inventory/expiry?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days, filterType]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const expired = items.filter(i => i.urgency === 'expired').length;
  const critical = items.filter(i => i.urgency === 'critical').length;
  const warning = items.filter(i => i.urgency === 'warning').length;
  const upcoming = items.filter(i => i.urgency === 'upcoming').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Expired', value: expired, color: 'text-red-700 bg-red-50' },
          { label: 'Critical (≤7d)', value: critical, color: 'text-red-600 bg-red-50' },
          { label: 'Warning (≤30d)', value: warning, color: 'text-yellow-600 bg-yellow-50' },
          { label: 'Upcoming', value: upcoming, color: 'text-blue-600 bg-blue-50' },
        ].map(s => (
          <Card key={s.label} className="p-4">
            <p className={`text-2xl font-bold font-manrope ${s.color.split(' ')[0]}`}>{s.value}</p>
            <p className="text-xs text-text-secondary font-ibm mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Lookahead Days</label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="font-ibm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[30, 60, 90, 180, 365].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Item Type</label>
            <Select value={filterType || '__all__'} onValueChange={v => setFilterType(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || items.length === 0} />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['SR #', 'Item Type', 'Manufacture', 'Location', 'Dates Expiring', 'Days Left', 'Urgency'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-text-secondary">No expiring items in the selected window</td></tr>
              ) : items.map(item => (
                <tr key={item.id} className={`hover:bg-gray-50/60 ${item.urgency === 'expired' ? 'bg-red-50/40' : item.urgency === 'critical' ? 'bg-orange-50/40' : ''}`}>
                  <td className="px-4 py-3 font-semibold text-text-dark font-ibm">{item.sr_no}</td>
                  <td className="px-4 py-3 text-text-dark">{item.item_type}</td>
                  <td className="px-4 py-3 text-text-dark">{item.manufacture || '-'}</td>
                  <td className="px-4 py-3 text-text-dark">{item.location || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      {(item.dates || []).map((d, i) => (
                        <p key={i} className="text-xs text-text-secondary">
                          <span className="font-medium">{d.label}:</span> {formatDate(d.date)}
                        </p>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold font-ibm">
                    {item.days_left < 0 ? <span className="text-red-600">{Math.abs(item.days_left)}d ago</span>
                      : <span className={item.days_left <= 7 ? 'text-red-600' : item.days_left <= 30 ? 'text-yellow-600' : 'text-blue-600'}>{item.days_left}d</span>}
                  </td>
                  <td className="px-4 py-3"><ExpiryBadge urgency={item.urgency} daysLeft={item.days_left} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab 3: Utilization Stats ──────────────────────────────────
function UtilizationStats() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filterType) p.append('item_type', filterType);
    fetch(`${API}/api/reports/inventory/utilization?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterType]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const totalCheckouts = items.reduce((s, i) => s + i.total_checkouts, 0);
  const inUse = items.filter(i => i.current_status === 'In Use').length;
  const neverUsed = items.filter(i => i.total_checkouts === 0).length;
  const mostUsed = items.length > 0 ? items[0] : null;

  const UTIL_COLS = [
    { header: 'SR #', key: 'sr_no' },
    { header: 'Item Type', key: 'item_type' },
    { header: 'Manufacture', key: 'manufacture' },
    { header: 'Location', key: 'location' },
    { header: 'Docket Checkouts', key: 'docket_checkouts' },
    { header: 'Trip Checkouts', key: 'trip_checkouts' },
    { header: 'Total Checkouts', key: 'total_checkouts' },
    { header: 'Last Used', key: 'last_used', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
    { header: 'Current Status', key: 'current_status' },
  ];
  const handleExport = () => exportToCsv('Inventory_Utilization', UTIL_COLS, items);
  const handlePrint = () => printReport('Inventory Utilization & Usage Stats', UTIL_COLS, items);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Checkouts', value: totalCheckouts, color: 'text-blue-600' },
          { label: 'Currently In Use', value: inUse, color: 'text-orange-600' },
          { label: 'Never Used', value: neverUsed, color: 'text-gray-500' },
          { label: 'Most Used', value: mostUsed ? `${mostUsed.sr_no} (${mostUsed.total_checkouts})` : '-', color: 'text-green-600', small: true },
        ].map(s => (
          <Card key={s.label} className="p-4">
            <p className={`${s.small ? 'text-base' : 'text-2xl'} font-bold font-manrope ${s.color}`}>{s.value}</p>
            <p className="text-xs text-text-secondary font-ibm mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="flex items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Item Type</label>
            <Select value={filterType || '__all__'} onValueChange={v => setFilterType(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || items.length === 0} />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['SR #', 'Item Type', 'Manufacture', 'Location', 'Docket Checkouts', 'Trip Checkouts', 'Total', 'Last Used', 'Current Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">No items found</td></tr>
              ) : items.map((item, idx) => (
                <tr key={item.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {idx === 0 && <TrendingUp className="w-3 h-3 text-green-500" />}
                      <span className="font-semibold text-text-dark font-ibm">{item.sr_no}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text-dark">{item.item_type}</td>
                  <td className="px-4 py-3 text-text-dark">{item.manufacture || '-'}</td>
                  <td className="px-4 py-3 text-text-dark">{item.location || '-'}</td>
                  <td className="px-4 py-3 text-center font-ibm">{item.docket_checkouts}</td>
                  <td className="px-4 py-3 text-center font-ibm">{item.trip_checkouts}</td>
                  <td className="px-4 py-3 text-center font-bold font-ibm">{item.total_checkouts}</td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{formatDate(item.last_used)}</td>
                  <td className="px-4 py-3">
                    {item.current_status === 'In Use'
                      ? <Badge className="bg-orange-500 text-white">In Use</Badge>
                      : <Badge className="bg-green-500 text-white">Idle</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────
const TABS = [
  { id: 'status', label: 'Status Board', icon: LayoutGrid },
  { id: 'expiry', label: 'Expiry Calendar', icon: CalendarDays },
  { id: 'utilization', label: 'Utilization Stats', icon: BarChart2 },
];

export default function InventoryReports() {
  const [activeTab, setActiveTab] = useState('status');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-dark font-manrope">Inventory Reports</h1>
        <p className="text-sm text-text-secondary mt-1">Track item status, expiry dates and usage across dockets and trips</p>
      </div>

      {/* Tab Nav */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-inventory-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-ibm border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-text-secondary hover:text-text-dark'
              }`}
            >
              <Icon className="w-4 h-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'status' && <StatusBoard />}
      {activeTab === 'expiry' && <ExpiryCalendar />}
      {activeTab === 'utilization' && <UtilizationStats />}
    </div>
  );
}
